import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import {
  LoginPage,
  InvitationAcceptPage,
  DashboardPage,
  UsersPage,
  CasesPage,
  CaseCreatePage,
  CaseEditPage,
  ClientsSimplePage as ClientsPage,
  ClientDetailPage,
  CreateClientPageMinimal as CreateClientPage,
  EditClientPage,
  ProfilePage,
  UnauthorizedPage,
  MetricsManager,
  TranslationsPage,
  LayoutsPage,
} from './pages';
import { SettingsPage } from './pages/SettingsPage';
import { AlertBannerPage } from './pages/AlertBannerPage';
import { TaxonomiesPage } from './pages/TaxonomiesPage';
import { ComponentTestPage } from './pages/ComponentTestPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { SessionWarning } from './components/SessionWarning';
import { GlobalErrorHandler } from './components/GlobalErrorHandler';
import BugReportButton from './components/BugReportButton';

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <GlobalErrorHandler />
              <SessionWarning />
              <BugReportButton />
              <Routes>
                {/* Public routes */}
                <Route path="/admin/login" element={<LoginPage />} />
                <Route
                  path="/invitation/accept/:token"
                  element={<InvitationAcceptPage />}
                />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route
                  path="/test/components"
                  element={<ComponentTestPage />}
                />

                {/* Protected routes */}
                <Route
                  path="/admin/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/cases"
                  element={
                    <ProtectedRoute>
                      <CasesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/cases/new"
                  element={
                    <ProtectedRoute requireAdmin>
                      <CaseCreatePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/cases/:id/edit"
                  element={
                    <ProtectedRoute requireAdmin>
                      <CaseEditPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/metrics"
                  element={
                    <ProtectedRoute requireAdmin>
                      <MetricsManager />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/clients"
                  element={
                    <ProtectedRoute>
                      <ClientsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/clients/new"
                  element={
                    <ProtectedRoute requireAdmin>
                      <CreateClientPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/clients/:id/edit"
                  element={
                    <ProtectedRoute requireAdmin>
                      <EditClientPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/clients/:id"
                  element={
                    <ProtectedRoute>
                      <ClientDetailPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/settings"
                  element={
                    <ProtectedRoute requireAdmin>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/translations"
                  element={
                    <ProtectedRoute requireAdmin>
                      <TranslationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/taxonomies"
                  element={
                    <ProtectedRoute requireAdmin>
                      <TaxonomiesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/layouts"
                  element={
                    <ProtectedRoute requireAdmin>
                      <LayoutsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/alert-banner"
                  element={
                    <ProtectedRoute requireAdmin>
                      <AlertBannerPage />
                    </ProtectedRoute>
                  }
                />

                {/* Default redirect */}
                <Route
                  path="/admin"
                  element={<Navigate to="/admin/dashboard" replace />}
                />
                <Route
                  path="/"
                  element={<Navigate to="/admin/dashboard" replace />}
                />
              </Routes>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};
